import styled from 'styled-components';
import { fontBody, fontAccent } from 'Styles/fonts';
import readableText from 'Styles/readable-text';
import bp from 'Styles/breakpoints';
import Layout from '../Layout/Layout';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';

/**
 *  Generates `grid-template-areas` rules
 *
 * @param {"bleed" | "inset"} imageStyle
 * @param {"left" | "right"} imagePosition
 * @param {Number} imageColumns - The amount of columns the image occupies
 * @param {Number} editorialColumns - The amount of columns the editorial occupies
 */
const generateTemplateAreas = (
  imageStyle,
  imagePosition,
  imageColumns,
  editorialColumns,
  largeScreen = false
) => {
  const gridTemplateArea = [
    ...new Array(editorialColumns).fill('editorial'),
    ...new Array(
      imageStyle === 'inset' && largeScreen ? imageColumns - 1 : imageColumns
    ).fill('image')
  ];

  if (imageStyle === 'inset' && largeScreen) {
    gridTemplateArea.push('.');
  }

  if (imagePosition === 'left') {
    gridTemplateArea.reverse();
  }

  return `
    grid-template-areas: '${gridTemplateArea.join(' ')}';
  `;
};

export const ContentBlock = styled(Layout)`
  width: 100%;

  /**
   * This block describes how the grid should be laid out
   * It uses the "layout" prop to describe how many columns the image and editorial should occupy in the grid
   *
   * e.g. 8:4 means 8 cols for image and 4 cols for editorial
   */
  ${({ $layout, $imagePosition, $imageStyle }) => {
    const [imageColumns, editorialColumns] = $layout.split(':');

    return `
      grid-template-areas: 'image image image image image image image image image image image image'
                           'editorial editorial editorial editorial editorial editorial editorial editorial editorial editorial editorial editorial';

      ${bp('medium')} {
        ${generateTemplateAreas($imageStyle, $imagePosition, 6, 6)}
      }

      ${
        $layout !== '7:5'
          ? `
          ${bp('large')} {
            ${generateTemplateAreas(
              $imageStyle,
              $imagePosition,
              imageColumns * 1,
              editorialColumns * 1,
              true
            )}
          }
        `
          : ''
      } 
    `;
  }}

  ${({ $backgroundColor }) => {
    if ($backgroundColor) {
      return `
      ${readableText($backgroundColor)}
    `;
    }
  }};
`;

export const ImageBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  grid-area: image;

  ${bp('medium')} {
    padding-top: unset;

    /* On medium sized screens images can shrink to where the Content Block layout looks weird. This forces the image to expand to the height of the container while giving it a slight crop */
    & > img {
      height: 100%;
      object-fit: cover;
    }
  }
`;

export const EditorialBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  grid-area: editorial;
  padding-bottom: 32px;

  ${bp('medium')} {
    ${({ hasTopPadding }) =>
      hasTopPadding ? 'padding: 48px 0 48px 0' : 'padding-bottom: 48px'};
  }

  ${bp('large')} {
    padding: ${({ hasTopPadding }) =>
      hasTopPadding ? '64px 48px 64px 48px' : '0 48px 64px 48px'};
    ${({ removePadding }) =>
      removePadding ? `padding-${removePadding}: 0;` : undefined}
  }
`;

export const InsetWrapper = styled(ContentBlock)`
  ${ImageBlock} {
    padding-top: ${({ hasTopPadding }) => hasTopPadding && '32px'};

    ${bp('medium')} {
      padding: ${({ hasTopPadding }) =>
        hasTopPadding ? '48px 0 48px 0' : '0 0 48px'};
    }

    ${bp('large')} {
      padding: ${({ hasTopPadding }) =>
        hasTopPadding ? '64px 0 64px 0' : '0 0 64px'};
    }
  }
`;

export const BleedWrapper = styled(ContentBlock)`
  ${ImageBlock} {
    margin-left: -24px;
    margin-right: -24px;
  }

  ${bp('medium')} {
    ${({ $imagePosition }) => {
      if ($imagePosition === 'left') {
        return `
        ${ImageBlock} {
          margin-left: -48px; 
          margin-right: -16px;
        }

        ${EditorialBlock} {
          margin-left: 16px;
        }
        `;
      }

      if ($imagePosition === 'right') {
        return `
        ${ImageBlock} {
          margin-right: -48px; 
          margin-left: -16px;
        }

        ${EditorialBlock} {
          margin-right: 16px;
        }
        `;
      }
    }}
  }
`;

export const Image = styled(ResponsiveImage)`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const RichText = styled.div`
  color: inherit;
  ${fontBody(16)}

  ${bp('large')} {
    ${fontBody(18)}
  }

  ${({ $hasBottomMargin }) =>
    $hasBottomMargin ? 'margin-bottom: 12px;' : null}

  & > :last-child {
    margin-bottom: 0;
  }

  & > h2 {
    margin-bottom: 4px;
    ${fontAccent(20)}

    ${bp('medium')} {
      ${fontAccent(24)}
    }

    ${bp('large')} {
      margin-bottom: 8px;
      ${fontAccent(32)}
    }
  }

  & > ul,
  & > ol,
  & > p {
    margin-bottom: 12px;

    ${bp('large')} {
      margin-bottom: 16px;
    }
  }

  & b {
    font-weight: 700;
  }

  & i {
    font-style: italic;
  }

  & a {
    color: inherit;
  }
`;

export const Icon = styled.img`
  height: 48px;
  margin-bottom: 8px;

  ${bp('large')} {
    height: 64px;
  }
`;

export default {};
